import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLazyQuery } from '@apollo/client/react';
import * as R from 'ramda';

import compare from '@atom/components/common/svgIcons/compare.svg';
import { GET_BUDGET_SUMMARIES } from '@atom/graph/budget';
import { ClickAwayListener, Icon, IconButton, Popper } from '@atom/mui';
import { InfoOutlinedIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import layout from '@atom/styles/layout';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailContext from './BudgetDetailContext';
import BudgetDetailOverviewComparison from './BudgetDetailOverviewComparison';
import BudgetDetailOverviewField from './BudgetDetailOverviewField';
import {
  AUTH_BUDGET_TOOLTIP_COPY,
  DIFFERENCE_TOOLTIP_COPY,
  FIXED_COST_TOOLTIP_COPY,
  HAS_DAMAGE_CLAIM_TOOLTIP_COPY,
} from './budgetDetailUtils';

import classes from './budgetDetail.module.css';

const styles = {
  popper: {
    backgroundColor: colors.neutral.white,
    boxShadow: layout.muiDropdownBoxShadow,
    zIndex: 100,
  },
  totalsRow: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '1.5rem',
    gap: '1.5rem',
  },
  totalBudgetLabel: {
    fontSize: fonts.sm,
    color: colors.neutral.dim,
    paddingBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  fixedInfoIconButton: {
    padding: '0',
  },
  fixedInfoIcon: { fontSize: '0.75rem' },
  totalBudgetAmount: {
    fontSize: fonts.lg,
    color: colors.neutral.dark,
    fontWeight: '500',
    gap: '0.5rem',
  },
  heading: {
    fontSize: fonts.lg,
    padding: '0.75rem 0',
    fontWeight: '500',
  },
  compareIcon: {
    width: '1.25rem',
  },
  hoverArea: {
    display: 'inlineBlock',
    minWidth: '7rem',
  },
};

const BudgetDetailOverview = () => {
  const {
    parentBudgetUnit,
    comparisonBudgets,
    budgetOverviewSummaries,
    setBudgetOverviewSummaries,
    categoryIds,
    budgetTypes,
    budgetItemTemplateNames,
    showTracking,
    showExpenditures,
    showAuthorizedBudget,
  } = useContext(BudgetDetailContext);

  const showDamageClaim = useMemo(
    () =>
      showExpenditures && !isNilOrEmpty(parentBudgetUnit?.damageClaimRevenue),
    [parentBudgetUnit, showExpenditures],
  );

  const loading = R.isNil(parentBudgetUnit);

  const tooltipCopy: string = useMemo(() => {
    return showDamageClaim
      ? HAS_DAMAGE_CLAIM_TOOLTIP_COPY
      : FIXED_COST_TOOLTIP_COPY;
  }, [showDamageClaim]);

  const [open, setOpen] = useState<boolean>(false);
  const [hovering, setHovering] = useState<boolean>(false);

  const anchorRef = useRef<HTMLDivElement>(null);

  const [
    fetchSummaries,
    { loading: loadingSummaries, data: overviewSummariesData },
  ] = useLazyQuery(GET_BUDGET_SUMMARIES, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (overviewSummariesData) {
      setBudgetOverviewSummaries(
        R.pathOr([], ['budgetSummary', 'budgetSummary'], overviewSummariesData),
      );
    }
  }, [overviewSummariesData]);

  useEffect(() => {
    if (
      open &&
      !isNilOrEmpty(comparisonBudgets) &&
      !loadingSummaries &&
      isNilOrEmpty(budgetOverviewSummaries)
    ) {
      fetchSummaries({
        variables: {
          input: {
            budgetUnitId: parentBudgetUnit.id,
            budgetIds: R.pluck('id', comparisonBudgets),
            categoryIds,
            itemNames: budgetItemTemplateNames,
            budgetTypes,
          },
        },
      });
    }
  }, [open]);

  const compareIcon = useMemo(
    () =>
      hovering || open ? (
        <img
          onClick={() => setOpen(!open)}
          className={classes.clickable}
          src={compare}
          style={styles.compareIcon}
        />
      ) : null,
    [hovering, open],
  );

  const hoverStyle = useMemo(
    () => ({
      ...styles.hoverArea,
      ...((hovering || open) && { fontWeight: 800 }),
    }),
    [hovering, open],
  );

  const getAuthorizedTotals = (): ReactNode => {
    if (!parentBudgetUnit?.authorizedBudget) {
      return null;
    }

    const absDifference = R.has('difference', parentBudgetUnit)
      ? Math.abs(parentBudgetUnit.difference)
      : null;

    const getDifferencePercent = (): string => {
      const hasValues =
        !isNilOrEmpty(parentBudgetUnit.totalBudget) &&
        !isNilOrEmpty(absDifference);

      return hasValues
        ? ` (${((absDifference / parentBudgetUnit.totalBudget) * 100).toFixed(2)}%)`
        : '';
    };

    return (
      <>
        <div>
          <div style={styles.totalBudgetLabel}>
            Authorized Budget
            <IconButton
              style={styles.fixedInfoIconButton}
              tooltip={AUTH_BUDGET_TOOLTIP_COPY}
            >
              <Icon style={styles.fixedInfoIcon} icon={InfoOutlinedIcon} />
            </IconButton>
          </div>
          <div style={styles.totalBudgetAmount}>
            {numberToLocaleString(parentBudgetUnit?.authorizedBudget)}
          </div>
        </div>
        <div>
          <div style={styles.totalBudgetLabel}>
            Difference
            <IconButton
              style={styles.fixedInfoIconButton}
              tooltip={DIFFERENCE_TOOLTIP_COPY}
              maxWidth={500}
            >
              <Icon style={styles.fixedInfoIcon} icon={InfoOutlinedIcon} />
            </IconButton>
          </div>
          <div style={styles.totalBudgetAmount}>
            {numberToLocaleString(absDifference)}
            {getDifferencePercent()}
          </div>
        </div>
      </>
    );
  };

  const remainingValue = useMemo(() => {
    const absVal = Math.abs(parentBudgetUnit?.remaining);
    const isNegative = parentBudgetUnit?.remaining < 0;
    const overflow = absVal > parentBudgetUnit?.totalBudget;
    const percentVal = overflow
      ? 100
      : Math.round((absVal / parentBudgetUnit?.totalBudget) * 100);
    const valueString = numberToLocaleString(parentBudgetUnit?.remaining);
    const percentString = `(${isNegative ? '-' : ''}${percentVal}%)`;
    return parentBudgetUnit?.remaining !== 0
      ? `${valueString} ${percentString}`
      : valueString;
  }, [parentBudgetUnit]);

  return (
    <>
      <div style={styles.heading}>Overview</div>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div style={hoverStyle}>
          <div
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            ref={anchorRef}
            style={styles.totalsRow}
          >
            <BudgetDetailOverviewField
              loading={loading}
              label="Total Budget"
              value={numberToLocaleString(parentBudgetUnit?.totalBudget)}
            />
            {showAuthorizedBudget && getAuthorizedTotals()}
            {showTracking && (
              <>
                <BudgetDetailOverviewField
                  loading={loading}
                  label="Total Actual"
                  value={numberToLocaleString(parentBudgetUnit?.actualBudget)}
                />
                <BudgetDetailOverviewField
                  loading={loading}
                  label="Future Fixed Cost"
                  value={numberToLocaleString(parentBudgetUnit?.fixedCosts)}
                />
                {showDamageClaim && (
                  <>
                    <BudgetDetailOverviewField
                      loading={loading}
                      label="Damage Claim Net Revenue"
                      value={numberToLocaleString(
                        parentBudgetUnit?.damageClaimRevenue,
                      )}
                    />
                    <BudgetDetailOverviewField
                      loading={loading}
                      label="Federal Net Revenue"
                      value={numberToLocaleString(
                        parentBudgetUnit?.federalRevenue,
                      )}
                    />
                  </>
                )}
                <BudgetDetailOverviewField
                  loading={loading}
                  label="Total Remaining"
                  icon={
                    <IconButton
                      style={styles.fixedInfoIconButton}
                      tooltip={tooltipCopy}
                    >
                      <Icon
                        style={styles.fixedInfoIcon}
                        icon={InfoOutlinedIcon}
                      />
                    </IconButton>
                  }
                  value={remainingValue}
                />
              </>
            )}
            {compareIcon}
          </div>

          <Popper
            open={open}
            anchorEl={anchorRef?.current}
            style={styles.popper}
            onClose={() => setOpen(false)}
            placement="bottom-start"
          >
            <BudgetDetailOverviewComparison
              setOpen={setOpen}
              loadingSummaries={loadingSummaries}
              budgetOverviewSummaries={budgetOverviewSummaries}
            />
          </Popper>
        </div>
      </ClickAwayListener>
    </>
  );
};

export default BudgetDetailOverview;
